import { useFormikContext } from 'formik'
import React, { useEffect } from 'react'
import { useDebounce } from 'use-debounce'

import { AutoComplete, VisuallyHiddenInput, withFormikSupport } from '@fourthwall/components'
import type { Option } from '@fourthwall/components/dist/inputs/AutoComplete/types'

import type { ProductFormState } from '../../../../SingleProduct.types'
import { HS_CODE_LENGTH } from '../../consts'
import { useHSCodesQuery } from '../queries/useHSCodesQuery'
import * as S from './styled'

const SEARCH_CHARS_MIN_LIMIT = 3
const SEARCH_CHARS_MAX_LIMIT = 50

type HsCodeSelectProps = {
  name: string
  label?: string
  suggestion?: string
  value: string | null
  error?: string
  onChange: (value: string) => void
}

const formatHSCode = (code: string | null) => {
  if (!code) return ''

  if (code.length <= 4) return code

  if (/^\d+$/.test(code)) {
    return `${code.slice(0, 4)}.${code.slice(4)}`
  }

  return code
}

const removeFormat = (code: string) => code.replace('.', '')

const HSCodeSelectComponent: React.FC<HsCodeSelectProps> = ({
  name,
  label,
  suggestion,
  value,
  error,
  onChange,
}) => {
  const { values, dirty, setFieldValue } = useFormikContext<ProductFormState>()
  const [debouncedValue] = useDebounce(value || '', 500)

  const isSearchValid =
    debouncedValue?.length >= SEARCH_CHARS_MIN_LIMIT &&
    debouncedValue.length < SEARCH_CHARS_MAX_LIMIT

  const { HSCodesQuery } = useHSCodesQuery([
    {
      categoryId: !isSearchValid && !!values.categoryId ? values.categoryId : undefined,
      search: isSearchValid ? debouncedValue : undefined,
    },
  ])

  useEffect(() => {
    if (!dirty || !value || value.length !== HS_CODE_LENGTH) return

    const hasHsCodes =
      HSCodesQuery.isFetching || (HSCodesQuery.data && HSCodesQuery.data.length > 0)

    setFieldValue('customsInformation.hasHsCodes', hasHsCodes)
  }, [value, HSCodesQuery.data, HSCodesQuery.isFetching, setFieldValue, dirty])

  const handleSelect = (option?: Option) => {
    if (option?.id) {
      onChange(option.id)
    }
  }

  const options =
    HSCodesQuery.data?.map((item) => ({
      id: item.code,
      content: (
        <S.Option>
          <S.OptionCode>{formatHSCode(item.code)}</S.OptionCode> - {item.description}
        </S.Option>
      ),
    })) || []

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = removeFormat(e.target.value)
    onChange(newValue)
  }

  return (
    <>
      <VisuallyHiddenInput name={name} />
      <AutoComplete
        label={label}
        options={options}
        optionsLabel={!debouncedValue ? 'Suggestions' : undefined}
        emptyState={<S.EmptyState>No results found</S.EmptyState>}
        suggestion={suggestion}
        value={formatHSCode(value)}
        error={error}
        withArrow
        maxLength={SEARCH_CHARS_MAX_LIMIT}
        isLoading={HSCodesQuery.isFetching}
        onChange={handleChange}
        onSelect={handleSelect}
      />
    </>
  )
}

export const HSCodeSelect = withFormikSupport(HSCodeSelectComponent)
